import React, { useEffect, useState } from "react";

import DefaultButton from "@/components/Buttons/DefaultButton/DefaultButton";
import DefaultFileButton from "@/components/Buttons/DefaultButton/DefaultFileButton";
import DefaultButtonDark from "@/components/Buttons/DefaultButtonDark/DefaultButtonDark";
import DefaultFileButtonDark from "@/components/Buttons/DefaultButtonDark/DefaultFileButtonDark";
import Title from "@/components/Title/Title";

import { IImage, ILink } from "@/src/types";
import { Wysiwyg } from "@/styles/typography";
import WhiteArrow from "../../../images/WhiteArrow.png";
import S from "./MainHero.styled";

import { useLocalization } from "@/contexts/Localization";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";

import { Navigation } from "swiper";
import EventCountDown from "@/components/EventCountDown/EventCountDown";
import { getImage, GatsbyImage } from "gatsby-plugin-image";

import fallBackVals from "./data";

import { graphql, useStaticQuery } from "gatsby";
import StickyContent from "./StickyContent";

export type IEvent = {
  title: string;
  location: string;
  description: string;
  registration_url: string;
  information_url: string;
  datetime_from: string;
  datetime_to: string;
  is_invite_only: boolean;
};

export type EventProps = {
  allPortalEvent?: {
    nodes: IEvent[];
  } | undefined;
};

export const useEventData = () => {
  const data: EventProps = useStaticQuery(
    graphql`
      {
        allPortalEvent(filter: { id: { ne: "dummy" } }) {
          nodes {
            title
            location
            description
            registration_url
            information_url
            datetime_from
            datetime_to
            is_invite_only
          }
        }
      }
    `
  );
  return data?.allPortalEvent?.nodes;
};

export interface Props {
  backgroundImage: IImage;
  video: {
    localFile: {
      publicURL: string;
    };
    mimeType: string;
  };
  mediaType: string;
  title: string;
  text: string;
  subtitle: string;
  buttons: {
    button1: {
      button: ILink;
      file: {
        mediaItemUrl: string;
      };
      fileTitle: string;
    };
    button2: {
      button: ILink;
      file: {
        mediaItemUrl: string;
      };
      fileTitle: string;
    };
  };
  linkTitle1: string;
  linkTitle2: string;
  linkTitle3: string;
  linkTitle4: string;
  link1: ILink;
  link2: ILink;
  link3: ILink;
  link4: ILink;
  file1: {
    title: string;
    mediaItemUrl: string;
  };
  file2: {
    title: string;
    mediaItemUrl: string;
  };
  file3: {
    title: string;
    mediaItemUrl: string;
  };
  file4: {
    title: string;
    mediaItemUrl: string;
  };
  eventEnabled: boolean;
  eventCountdown: {
    dateTimePicker: string;
    eventTitle: string;
  };
  menuLinks: string;
  heroButtonColor?: string;
  sticky?: {
    content: string;
    title: string;
    color: string;
  };
}

const Link = ({ link, title }: { link: ILink; title: string }) => (
  <S.Link className={(typeof window !== "undefined") && window.location.pathname === link.url ? "active" : ""} href={link.url}>
    <S.LinkWrapper>
      <S.LinkTitle>{title}</S.LinkTitle>
      <S.LinkText className="size">
        {link.title} <img src={WhiteArrow} alt="arrow" />
      </S.LinkText>
    </S.LinkWrapper>
  </S.Link>
);

const File = ({
  file,
  title,
}: {
  file: { title: string; mediaItemUrl: string };
  title: string;
}) => (
  <S.Link href={file.mediaItemUrl} target="_blank" download>
    <S.LinkWrapper>
      <S.LinkTitle>{title}</S.LinkTitle>
      <S.LinkText>
        Ladda ner <img src={WhiteArrow} alt="arrow" />
      </S.LinkText>
    </S.LinkWrapper>
  </S.Link>
);


const renderSticky = (stickyContent: {
  url: string; title: string; 
}[], heroButtonColor: string | undefined) => {
  
  return (
    <S.ButtonWrapper key={stickyContent[0].title}>
      <DefaultButtonDark
        button={{ url: stickyContent[0].url, title: stickyContent[0].title.length > 20 ? stickyContent[0].title.slice(0, 20) + '...' : stickyContent[0].title, target: 'blank' }}
        title={stickyContent[0].title}
        background={heroButtonColor}
        sticky={true}
        fontSize={'16px'}
        padding={'8px 23px'}
      >
        <S.Img className="arrow_btn" src={ExternalLinkArrow} alt="arrow" />
        <S.Img className="arrow_btn-active" src={ExternalLinkArrowActive} alt="arrow" />
      </DefaultButtonDark>
    </S.ButtonWrapper>
  )
}

const MainHero: React.FC<Props> = ({
  backgroundImage,
  text,
  title,
  subtitle,
  buttons,
  linkTitle1,
  linkTitle2,
  linkTitle3,
  linkTitle4,
  link1,
  link2,
  link3,
  link4,
  file1,
  file2,
  file3,
  file4,
  eventEnabled,
  eventCountdown,
  mediaType,
  video,
  menuLinks,
  heroButtonColor,
  sticky
}) => {

  const handleOnClick = () => {
    window.scrollTo({
      top: window.innerHeight / 2,
      behavior: "smooth",
    });
  };

  const [showEventCountdown, setShowEventCountdown] = useState(false);
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [menuLinkValues, _] = useState(menuLinks ? JSON.parse(`${menuLinks}`) : fallBackVals);

  useEffect(() => {
    if (eventCountdown.dateTimePicker) {
      const date = new Date(eventCountdown.dateTimePicker);
      const now = new Date();
      if (now < date && eventEnabled) {
        setShowEventCountdown(true);
      }
    }

  }, [eventCountdown.dateTimePicker]);

  const imageData = backgroundImage?.gatsbyImage
    ? getImage(backgroundImage.gatsbyImage)
    : null;

  const { language } = useLocalization();

  return (
    <S.Container>
      {mediaType === "image" && imageData && (
        <GatsbyImage
          className="gatsby-image"
          image={imageData}
          alt={backgroundImage.altText}
          loading="eager"
        />
      )}
      {mediaType === "video" && video && (
        <S.VideoContainer>
          <video autoPlay loop muted playsInline>
            <source src={video.localFile.publicURL} type={video.mimeType} />
          </video>
        </S.VideoContainer>
      )}
      <div className="main-inner">
        <S.GridContainer>
          <S.GridItem>
            <S.Title>
              {subtitle && <S.StubTitle>{subtitle}</S.StubTitle>}
              <Title title={title} backgroundColor={"black"} fontSize={"h1"} />
            </S.Title>
            <S.TextWrapper>
              {text && (
                <Wysiwyg
                  color="#f9f9f9"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              )}
            </S.TextWrapper>
          </S.GridItem>
          {sticky && sticky.content !== 'none' &&
            <StickyContent sticky={sticky} heroButtonColor={heroButtonColor} />
          }
          {showEventCountdown && (
            <>
              {eventCountdown.dateTimePicker && (
                <S.GridItem>
                  <EventCountDown event={eventCountdown} />
                </S.GridItem>
              )}
            </>
          )}
        </S.GridContainer>
        <S.Wrapper>
          {buttons.button1 && buttons.button1.file ? (
            <DefaultFileButtonDark
              url={buttons.button1.file.mediaItemUrl}
              title={buttons.button1.fileTitle}
            />
          ) : (
            buttons.button1?.button && (
              <DefaultButtonDark title={""} button={buttons.button1.button} background={heroButtonColor} />
            )
          )}
          {buttons.button2 && buttons.button2.file ? (
            <DefaultFileButton
              url={buttons.button2.file.mediaItemUrl}
              title={buttons.button2.fileTitle}
            />
          ) : (
            buttons.button2?.button && (
              <DefaultButton button={buttons.button2.button} />
            )
          )}
          <S.PulsatingCircle onClick={handleOnClick}>
            <S.ArrowDown src={WhiteArrow} alt="arrow" />
          </S.PulsatingCircle>
        </S.Wrapper>
      </div>
      <S.LinkContainer>
        {file1 ? (
          <File file={file1} title={linkTitle1} />
        ) : (
            menuLinkValues && <Link link={language === 'no' ? menuLinkValues.NO.member : (language === 'en' ? menuLinkValues.EN.member : menuLinkValues.SE.member)} title={language === 'no' ? menuLinkValues.NO.member.text : (language === 'en' ? menuLinkValues.EN.member.text : menuLinkValues.SE.member.text)} />
        )}
        {file2 ? (
          <File file={file2} title={linkTitle2} />
        ) : (
            menuLinkValues && <Link link={language === 'no' ? menuLinkValues.NO.press : (language === 'en' ? menuLinkValues.EN.press : menuLinkValues.SE.press)} title={language === 'no' ? menuLinkValues.NO.press.text : (language === 'en' ? menuLinkValues.EN.press.text : menuLinkValues.SE.press.text)} />
        )}
        {file3 ? (
          <File file={file3} title={linkTitle3} />
        ) : (
            menuLinkValues && <Link link={language === 'no' ? menuLinkValues.NO.events : (language === 'en' ? menuLinkValues.EN.events : menuLinkValues.SE.events)} title={language === 'no' ? menuLinkValues.NO.events.text : (language === 'en' ? menuLinkValues.EN.events.text : menuLinkValues.SE.events.text)} />
        )}
        {file4 ? (
          <File file={file4} title={linkTitle4} />
        ) : (
            menuLinkValues && <Link link={language === 'no' ? menuLinkValues.NO.insights : (language === 'en' ? menuLinkValues.EN.insights : menuLinkValues.SE.insights)} title={language === 'no' ? menuLinkValues.NO.insights.text : (language === 'en' ? menuLinkValues.EN.insights.text : menuLinkValues.SE.insights.text)} />
        )}
      </S.LinkContainer>
      <S.MobileLinkContainer>
        <Swiper navigation={true} modules={[Navigation]} className="mySwiper">
          {file1 ? (
            <SwiperSlide>
              <File file={file1} title={linkTitle1} />
            </SwiperSlide>
          ) : (
            link1 && (
              <SwiperSlide>
                <Link link={link1} title={linkTitle1} />
              </SwiperSlide>
            )
          )}
          {file2 ? (
            <SwiperSlide>
              <File file={file2} title={linkTitle2} />
            </SwiperSlide>
          ) : (
            link2 && (
              <SwiperSlide>
                <Link link={link2} title={linkTitle2} />
              </SwiperSlide>
            )
          )}
          {file3 ? (
            <SwiperSlide>
              <File file={file3} title={linkTitle3} />
            </SwiperSlide>
          ) : (
            link3 && (
              <SwiperSlide>
                <Link link={link3} title={linkTitle3} />
              </SwiperSlide>
            )
          )}
          {file4 ? (
            <SwiperSlide>
              <File file={file4} title={linkTitle4} />
            </SwiperSlide>
          ) : (
            link4 && (
              <SwiperSlide>
                <Link link={link4} title={linkTitle4} />
              </SwiperSlide>
            )
          )}
        </Swiper>
      </S.MobileLinkContainer>
    </S.Container>
  );
};

export default MainHero;
